import React, { useEffect, useRef, useState } from "react";
import styles from "./AddUsers.module.css";
import { FiAlertCircle, FiUpload } from "react-icons/fi";

interface AddUsersProps {
  formData: any;
  setFormData: (data: any) => void;
  disabled?: boolean;
}

const AddUsers = ({
  formData,
  setFormData,
  disabled = false,
}: AddUsersProps) => {
  const [inputValue, setInputValue] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [contextValue, setContextValue] = useState<string[]>([]);
  const [duplicateValues, setDuplicateValues] = useState<string[]>([]);
  const [fileLoadProgress, setFileLoadProgress] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
    setFormData({ ...formData, contextValues: contextValue });
  }, [contextValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setInputValue(e.target.value);
  };

  const addUsers = (userContextIds: string[]) => {
    if (disabled) return;
    const duplicateContexts = userContextIds.filter((id) =>
      contextValue.includes(id)
    );
    const uniqueContexts = userContextIds.filter(
      (id) => !contextValue.includes(id)
    );

    if (!!duplicateContexts.length) {
      setDuplicateValues(duplicateContexts);
      setTimeout(() => setDuplicateValues([]), 3000);
    }

    if (!!uniqueContexts.length) {
      setContextValue((prev) => [...prev, ...uniqueContexts]);
    }
  };

  const handleAddUsers = (): void => {
    if (disabled || !inputValue.trim()) return;

    const userContextIds = inputValue
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    addUsers(userContextIds);

    setInputValue("");
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setFileLoadProgress(0);

    try {
      const reader = new FileReader();
      const chunkSize = 1024 * 1024; // 1MB chunks
      const fileSize = file.size;
      let offset = 0;
      const chunks: string[] = [];

      const readChunk = () => {
        const slice = file.slice(offset, offset + chunkSize);
        reader.readAsText(slice);
      };

      reader.onload = (e) => {
        if (e.target?.result) {
          chunks.push(e.target.result as string);
          offset += chunkSize;

          // Calculate progress
          const progress = Math.min(Math.round((offset / fileSize) * 100), 100);
          setFileLoadProgress(progress);

          if (offset < fileSize) {
            readChunk(); // Read next chunk
          } else {
            // Process all chunks
            const result = chunks.join("");
            const idsFromFile = result
              .split(/[\n,]/)
              .map((id) => id.trim())
              .filter(Boolean);

            addUsers(idsFromFile);
            setIsUploading(false);
            setFileLoadProgress(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
        setIsUploading(false);
        setFileLoadProgress(null);
      };

      readChunk(); // Start reading first chunk
    } catch (error) {
      console.error("Error processing file:", error);
      setIsUploading(false);
      setFileLoadProgress(null);
    }
  };

  const handleRemoveUser = (id: string) => {
    if (disabled) return;
    setContextValue(contextValue.filter((userId) => userId !== id));
  };

  const handleUserKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      handleAddUsers();
    }
  };
  const isDisabled = disabled || isUploading;

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };
  const shouldRenderSuggestion = !!inputValue?.trim() && !disabled;

  return (
    <div
      className={`${styles.userIdContainer} ${disabled ? styles.disabled : ""}`}
    >
      <div className={styles.userIdList} ref={containerRef}>
        {contextValue.map((userId, userIndex) => (
          <span key={userIndex} className={styles.userIdTag}>
            {userId}
            <button
              type="button"
              className={styles.removeTag}
              onClick={() => handleRemoveUser(userId)}
              disabled={disabled}
            >
              ×
            </button>
          </span>
        ))}
        <div className={styles.inputWrapper}>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleUserKeyPress}
            className={styles.userIdInput}
            placeholder="Type or paste comma-separated values"
            disabled={isDisabled}
          />

          <button
            type="button"
            className={`${styles.uploadButton} ${
              isUploading ? styles.uploading : ""
            }`}
            onClick={handleUploadButtonClick}
            disabled={isDisabled}
          >
            <FiUpload size={14} />
            {isUploading ? "Uploading..." : "Upload"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileUpload}
            style={{ display: "none" }}
            disabled={isDisabled}
          />
        </div>
      </div>
      {!!duplicateValues.length && (
        <div className={styles.errorNudge}>
          <FiAlertCircle />
          {duplicateValues.length === 1
            ? `User ID "${duplicateValues[0]}" already exists`
            : `User IDs ${duplicateValues
                .map((id) => `"${id}"`)
                .join(", ")} already exist`}
        </div>
      )}
      {shouldRenderSuggestion && (
        <div className={styles.suggestion} onClick={handleAddUsers}>
          <span className={styles.addIcon}>+</span>
          {`Add "${inputValue}"`}
        </div>
      )}
      {fileLoadProgress !== null && (
        <div className={styles.fileProgress}>
          <div className={styles.fileProgressContainer}>
            <div
              className={styles.fileProgressBar}
              style={{ width: `${fileLoadProgress}%` }}
            />
          </div>
          <div className={styles.fileProgressLabel}>
            Loading file... {fileLoadProgress}%
          </div>
        </div>
      )}
    </div>
  );
};

export default AddUsers;
